import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import CustomInput from "../../Components/Custom/CustomInput";
import CustomButton from "../../Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { ProfileArgs } from "types/auth.api";
import { profileSetup } from "slices/auth/thunk";
import { redirectPage } from "utils/redirectPage";
import { GlobalImage } from "config/image";
import { compressImage } from "utils/compressImage";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  createNameValidation,
} from "Components/constants/validation";
import { ErrorToast } from "utils/toast";

const ProfileSetup = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, user } = useAppSelector((state) => state?.auth);
  const [selectedImage, setSelectedImage] = useState<string>(
    GlobalImage?.userPlaceHolder
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImageUpload = async (e: any) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    // Validate file type - only allow JPEG and PNG
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["jpg", "jpeg", "png"];

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || "")
    ) {
      e.target.value = ""; // Reset the input
      ErrorToast("Only JPEG and PNG images are allowed");
      return;
    }

    const compressed = await compressImage(file);
    setSelectedFile(compressed);
    console.log(compressed);

    const previewUrl = URL.createObjectURL(compressed);

    setSelectedImage(previewUrl);
  };

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const onBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters except + and ()
    const cleanValue = value.replace(/[^\d]/g, "");

    // Build the formatted value
    let formattedValue = "";
    if (cleanValue.length > 0) {
      // Add country code
      const countryCode = cleanValue.startsWith("44")
        ? cleanValue.substring(0, 2)
        : "44";
      const phoneNumber = cleanValue.startsWith("44")
        ? cleanValue.substring(2)
        : cleanValue;

      // Format as (+44) XXXXXXXXXX for display (with space)
      formattedValue = `(+${countryCode}) ${phoneNumber}`;
    }

    // Update formik with formatted value for display
    validation.setFieldValue("phone", formattedValue);
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      phone: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH("Name", VALIDATION_LIMITS.NAME.MIN)
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH("Name", VALIDATION_LIMITS.NAME.MAX)
        )
        .test(
          "valid-name",
          createNameValidation("Name").message,
          createNameValidation("Name").test
        ),

      phone: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Phone Number"))
        .matches(VALIDATION_PATTERNS.PHONE, VALIDATION_MESSAGES.INVALID_PHONE),
    }),
    onSubmit: async (values) => {
      // Clean phone number for backend (remove parentheses and spaces, keep +44)
      const cleanPhone = values.phone.replace(/[()]/g, "").replace(/\s+/g, "");

      const profileSetupPayload: ProfileArgs = {
        name: values?.name?.trim(),
        phone: cleanPhone,
        _method: "put",
        avatar: selectedFile,
      };
      try {
        const res = await dispatch(profileSetup(profileSetupPayload))?.unwrap();
        const route = redirectPage(res?.payload, true);
        if (route) {
          navigate(route, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {}
    },
  });

  document.title = "Profile Setup | EaziQuote";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>

            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  <div className="text-center mt-2">
                    <h5 className="text-black2D font-primary fw-semibold fs-24">
                      Profile Setup
                    </h5>
                  </div>

                  <div className="profile-img-container">
                    <div className="profile-img-wrapper position-relative d-inline-block">
                      <img
                        src={selectedImage}
                        alt="Profile"
                        className="rounded-circle profile-img object-fit-cover"
                        width={100}
                        height={100}
                        style={{ objectFit: "cover" }}
                      />
                      <label className="edit-profile-btn position-absolute bg-primary btn-press-effect">
                        <i
                          className="ri-add-line text-white"
                          style={{ fontSize: 20, marginTop: 2 }}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: "none" }}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="p-2">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <CustomInput
                        label="Name"
                        placeholder="Enter your name"
                        name="name"
                        type="text"
                        value={validation.values.name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.name}
                        error={validation.errors.name}
                      />
                      <CustomInput
                        label="Phone"
                        placeholder="Enter phone number"
                        name="phone"
                        type="tel"
                        value={validation.values.phone}
                        onChange={handlePhoneChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.phone}
                        error={validation.errors.phone}
                      />

                      <CustomButton
                        text="Continue"
                        type="submit"
                        loading={isLoading}
                        className="auth-Button"
                      />
                    </form>
                  </div>
                </AuthCard>
              </Col>
            </Row>
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ProfileSetup;
